import { createAsyncThunk } from '@reduxjs/toolkit';
import { HttpHandler } from '../../api/HttpHandler';
import { RootState } from '../index';
import { SPECIALISTS_COUNT_PER_PAGE } from './constants';
import { environment } from '../../enviroments';
import { getFiltersQuery, getSortKey } from '@mindly/ui-components';
import { FiltersData } from 'src/shared/types/filters';
import { getFilters, getPresetedFiltersValues, getSavedFilters, migrateSavedFilters } from 'src/shared/utils/filters';
import { TFunction } from 'i18next';
import { ISpecialist } from './types';
import { getMatchingValues } from 'src/shared/utils/specialist';
import { getQueryParams } from 'src/shared/utils/params';

export const fetchSpecialistsList = createAsyncThunk(
  'specialists/fetchSpecialistsList',
  async (locale: string, thunkAPI) => {
    const { specialists } = thunkAPI.getState() as RootState;

    const { limit, offset, sort } = specialists ?? {
      limit: SPECIALISTS_COUNT_PER_PAGE,
      offset: 0,
    };

    const filters = getFiltersQuery(specialists?.filters.values ?? {});

    const queryParams = {
      limit,
      offset,
      ...(filters.length > 0 && { filters }),
      sort: [getSortKey(sort.value, sort.direction)],
    };

    const queryString = getQueryParams(queryParams);

    const { data: response } = await HttpHandler<{ data: { list: ISpecialist[]; total: number } }>({
      method: 'GET',
      url: `${environment.SPECIALIST_APP_URL}/specialists/find/?${queryString}`,
    });

    const list = response?.data?.list.map((specialist) => ({
      ...specialist,
      ...getMatchingValues(specialists?.filters.values, specialist),
    }));

    return { list, total: response?.data?.total };
  }
);

export const fetchSpecialistsFilters = createAsyncThunk(
  'specialists/fetchSpecialistsFilters',
  async (
    { t, isLocalizedAreasOfWorkByClientProblem }: { t: TFunction; isLocalizedAreasOfWorkByClientProblem: boolean },
    thunkAPI
  ) => {
    const { user } = thunkAPI.getState() as RootState;
    if (!user) {
      return { filters: [], filtersValues: {} };
    }
    const { data: response } = await HttpHandler<{ data: FiltersData }>({
      method: 'GET',
      url: `${environment.SPECIALIST_FILTERS_URL}/filters?isLocalizedAreasOfWorkByClientProblem=${isLocalizedAreasOfWorkByClientProblem}`,
    });
    migrateSavedFilters(user.app_lang, user.id ?? '');
    const savedFilters = getSavedFilters(user.app_lang, user.id ?? '');
    const presetFiltersValues = getPresetedFiltersValues(response.data, user);
    const mergedFiltersValues = {
      ...presetFiltersValues,
      ...savedFilters,
    };
    const filters = getFilters(t, mergedFiltersValues, user, response.data);
    const goals =
      response.data.filterObject.localized_areas_of_work_by_client_problem?.map((item) => item.answer_title) || [];

    return { filters, filtersValues: mergedFiltersValues, goals };
  }
);

export const fetchSpecialistsBackupList = createAsyncThunk(
  'specialists/fetchSpecialistsBackupList',
  async (_, thunkAPI) => {
    const { user } = thunkAPI.getState() as RootState;
    const queryString = getQueryParams({
      limit: '40',
      ...(user?.global_personal_price && { sort: ['session_price:asc'] }),
    });
    const { data: response } = await HttpHandler({
      method: 'GET',
      url: `${environment.SPECIALIST_APP_URL}/specialists/find/?${queryString}`,
    });
    return response?.data;
  }
);
